import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY } from '../utils/constants';
import { Button, Field, Panel } from './ui';
import { toDisplayDate } from './Events';

// ─── Event payments (Stage 4) ────────────────────────────────────────────────
// Append-only money against an event. Every write goes through the Stage 1
// RPC record_event_payment (SECURITY DEFINER); the table grants staff SELECT
// only, so there is no insert, update or delete path from here, even by
// mistake. A refund is a negative row dated when the money went out. A
// mistake is corrected with a reversing row. Nothing is edited or deleted.
//
// Sum of amounts is what the event holds. The agreed total is derived from
// event_line_items (never stored) and shown only so outstanding can be read.
// This money reports in the Collections report by payment_date and nowhere
// else (not Net Collection, not Cash in Hand: those are per-sailing figures).

export const METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi',  label: 'UPI' },
  { value: 'bank', label: 'Bank transfer' },
  { value: 'card', label: 'Card' },
];
export const methodLabel = (v) => (METHODS.find(m => m.value === v) || {}).label || v || '';

const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
export const fmtSigned = (n) => (n < 0 ? `−₹${fmt(-n)}` : `₹${fmt(n)}`);
const todayInput = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};
const blankForm = () => ({ amount: '', date: todayInput(), method: 'cash', reference: '', note: '' });

const figure = (label, value, color) => (
  <div key={label} style={{ minWidth: 120 }}>
    <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
  </div>
);

export default function EventPayments({ eventId, agreedAmount }) {
  const [rows, setRows] = useState(null);
  const [mode, setMode] = useState(null); // 'payment' | 'refund' | null
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const { data, error: err } = await supabase
      .from('event_payments')
      .select('id, amount, payment_date, method, reference, note, created_at')
      .eq('event_id', eventId)
      .order('payment_date', { ascending: true })
      .order('created_at', { ascending: true });
    if (err) { setError(`Could not load payments: ${err.message}`); setRows([]); return; }
    setRows(data || []);
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const received = (rows || []).reduce((s, r) => s + Number(r.amount), 0);
  const agreed = agreedAmount === '' || agreedAmount == null ? null : Number(agreedAmount);
  const outstanding = agreed == null ? null : Math.max(agreed - received, 0);
  const over = agreed != null && received > agreed ? received - agreed : 0;

  const open = (m) => { setMode(m); setError(''); setForm(blankForm()); };
  const close = () => { setMode(null); setError(''); };
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    const amt = Number(form.amount);
    if (!(amt > 0)) { setError('Enter an amount above zero.'); return; }
    if (!form.date) { setError('Pick the date the money moved.'); return; }
    setSaving(true);
    setError('');
    // The RPC is the only write path and holds the rules: not in the future,
    // a refund never larger than what the event holds. Its message is shown
    // as-is when it refuses.
    const { error: err } = await supabase.rpc('record_event_payment', {
      p_event_id: eventId,
      p_amount: mode === 'refund' ? -amt : amt,
      p_payment_date: form.date,
      p_method: form.method,
      p_reference: form.reference.trim() || null,
      p_note: form.note.trim() || null,
    });
    setSaving(false);
    if (err) { setError(`Not recorded: ${err.message}`); return; }
    setMode(null);
    setForm(blankForm());
    load();
  };

  const isRefund = mode === 'refund';

  return (
    <Panel as="section" style={{ marginTop: 24 }} aria-labelledby="ev-pay-title">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <h3 id="ev-pay-title" style={{ fontSize: 'var(--text-base)', fontWeight: 700, color: NAVY, margin: 0 }}>Payments</h3>
        {mode === null && (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="primary" size="sm" onClick={() => open('payment')} disabled={rows === null}>Record payment</Button>
            <Button variant="secondary" size="sm" onClick={() => open('refund')} disabled={rows === null || received <= 0}>Record refund</Button>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginBottom: 16 }}>
        {figure('Agreed', agreed == null ? 'No lines yet' : fmtSigned(agreed), NAVY)}
        {figure('Received', fmtSigned(received), received > 0 ? '#166534' : NAVY)}
        {figure('Outstanding', outstanding == null ? '—' : fmtSigned(outstanding), outstanding ? '#92400e' : NAVY)}
      </div>
      {over > 0 && (
        <p style={{ margin: '-6px 0 14px', fontSize: 'var(--text-sm)', color: 'var(--color-warning-strong)' }}>
          Received is {fmtSigned(over)} more than the agreed amount.
        </p>
      )}

      {mode !== null && (
        <Panel tone="sunken" style={{ marginBottom: 16 }} role="group" aria-labelledby="ev-pay-form-title">
          <div id="ev-pay-form-title" style={{ fontWeight: 700, color: NAVY, fontSize: 'var(--text-base)', marginBottom: 4 }}>
            {isRefund ? 'Record a refund' : 'Record a payment'}
          </div>
          <p style={{ margin: '0 0 12px', fontSize: 'var(--text-sm)', color: 'var(--color-ink-secondary)' }}>
            {isRefund
              ? `Money going back out. Dated the day it left. This event holds ${fmtSigned(received)}.`
              : 'Money that has actually reached us, dated the day it did.'}
          </p>
          <div className="form-grid">
            <Field label="Amount" htmlFor="ev-pay-amount" required>
              <input id="ev-pay-amount" type="number" inputMode="decimal" min="0" step="1" value={form.amount} onChange={set('amount')} placeholder="0" />
            </Field>
            <Field label={isRefund ? 'Refunded on' : 'Received on'} htmlFor="ev-pay-date" required>
              <input id="ev-pay-date" type="date" value={form.date} max={todayInput()} onChange={set('date')} style={{ colorScheme: 'light' }} />
            </Field>
            <Field label="Method" htmlFor="ev-pay-method" required>
              <select id="ev-pay-method" value={form.method} onChange={set('method')}>
                {METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </Field>
            <Field label="Reference" htmlFor="ev-pay-ref" hint="UTR, receipt number, cheque number.">
              <input id="ev-pay-ref" type="text" value={form.reference} onChange={set('reference')} autoComplete="off" />
            </Field>
            <Field label="Note" htmlFor="ev-pay-note" fullWidth>
              <input id="ev-pay-note" type="text" value={form.note} onChange={set('note')} placeholder={isRefund ? 'Why it went back' : 'Advance, balance, second instalment'} autoComplete="off" />
            </Field>
          </div>
          <div className="ev-form-actions" style={{ marginTop: 12 }}>
            <Button variant={isRefund ? 'danger' : 'primary'} onClick={submit} disabled={saving}>
              {saving ? 'Recording' : isRefund ? 'Record refund' : 'Record payment'}
            </Button>
            <Button variant="secondary" onClick={close} disabled={saving}>Cancel</Button>
          </div>
        </Panel>
      )}

      {error && <div className="toast toast-error" role="alert" style={{ marginBottom: 12 }}>{error}</div>}

      {rows === null ? (
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--color-ink-faint)' }}>Loading payments</p>
      ) : rows.length === 0 ? (
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--color-ink-muted)' }}>No money recorded yet.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Date</th>
                <th style={{ textAlign: 'left' }}>Method</th>
                <th style={{ textAlign: 'left' }}>Reference</th>
                <th style={{ textAlign: 'left' }}>Note</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const amt = Number(r.amount);
                return (
                  <tr key={r.id}>
                    <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{toDisplayDate(r.payment_date)}</td>
                    <td>{methodLabel(r.method)}</td>
                    <td>{r.reference || ''}</td>
                    <td>{r.note || ''}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: amt < 0 ? '#dc2626' : 'var(--color-ink-brand)', whiteSpace: 'nowrap' }}>
                      {fmtSigned(amt)}{amt < 0 && <span style={{ marginLeft: 6, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.3px', textTransform: 'uppercase', padding: '1px 6px', borderRadius: 4, background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b' }}>Refund</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} style={{ fontWeight: 700 }}>Held by this event</td>
                <td style={{ textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: NAVY }}>{fmtSigned(received)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <p style={{ margin: '12px 0 0', fontSize: 'var(--text-xs)', color: 'var(--color-ink-faint)', lineHeight: 1.5 }}>
        Each line is money that moved on that date. A refund is a negative line dated the day it went out.
        A mistake is corrected by recording the reverse, nothing here is edited or deleted.
        This money shows in the Collections report on these dates and in no other figure.
      </p>
    </Panel>
  );
}
